import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '../../services/auth.service';
import { environment } from '../../../environments/environment';

// Custom validator to check passwords match
function passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const newPassword = control.get('newPassword');
    const confirmPassword = control.get('confirmPassword');

    if (newPassword && confirmPassword && newPassword.value !== confirmPassword.value) {
        confirmPassword.setErrors({ passwordMismatch: true });
        return { passwordMismatch: true };
    } else {
        if (confirmPassword?.errors?.['passwordMismatch']) {
            const errors = { ...confirmPassword.errors };
            delete errors['passwordMismatch'];
            confirmPassword.setErrors(Object.keys(errors).length ? errors : null);
        }
        return null;
    }
}

@Component({
    selector: 'app-change-password',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule],
    templateUrl: './change-password.component.html',
    styleUrl: './change-password.component.scss'
})
export class ChangePasswordComponent implements OnInit {
    changePasswordForm!: FormGroup;
    isLoading = false;
    errorMessage = '';
    successMessage = '';

    showCurrentPassword = false;
    showNewPassword = false;
    showConfirmPassword = false;

    // Password strength
    passwordStrength: 'weak' | 'medium' | 'strong' | '' = '';
    passwordStrengthScore = 0;

    private readonly API_URL = environment.apiUrl;

    constructor(
        private fb: FormBuilder,
        private authService: AuthService,
        private router: Router,
        private http: HttpClient,
        private cdr: ChangeDetectorRef
    ) { }

    ngOnInit(): void {
        this.initializeForm();
    }

    private initializeForm(): void {
        this.changePasswordForm = this.fb.group({
            currentPassword: ['', [Validators.required, Validators.minLength(6)]],
            newPassword: ['', [
                Validators.required,
                Validators.minLength(8),
                Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/)
            ]],
            confirmPassword: ['', [Validators.required]]
        }, { validators: passwordMatchValidator });

        // Watch new password to evaluate strength
        this.changePasswordForm.get('newPassword')?.valueChanges.subscribe(value => {
            this.evaluatePasswordStrength(value);
        });
    }

    private evaluatePasswordStrength(password: string): void {
        if (!password) {
            this.passwordStrength = '';
            this.passwordStrengthScore = 0;
            return;
        }

        let score = 0;
        if (password.length >= 8) score++;
        if (password.length >= 12) score++;
        if (/[a-z]/.test(password)) score++;
        if (/[A-Z]/.test(password)) score++;
        if (/\d/.test(password)) score++;
        if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) score++;

        this.passwordStrengthScore = Math.min(score, 6);

        if (score <= 2) {
            this.passwordStrength = 'weak';
        } else if (score <= 4) {
            this.passwordStrength = 'medium';
        } else {
            this.passwordStrength = 'strong';
        }
    }

    get strengthBarWidth(): string {
        return `${(this.passwordStrengthScore / 6) * 100}%`;
    }

    toggleVisibility(field: 'current' | 'new' | 'confirm'): void {
        if (field === 'current') this.showCurrentPassword = !this.showCurrentPassword;
        if (field === 'new') this.showNewPassword = !this.showNewPassword;
        if (field === 'confirm') this.showConfirmPassword = !this.showConfirmPassword;
    }

    onSubmit(): void {
        if (this.changePasswordForm.invalid) {
            this.markFormGroupTouched();
            return;
        }

        const { currentPassword, newPassword } = this.changePasswordForm.value;
        const currentUser = this.authService.getCurrentUser();

        if (!currentUser?.id) {
            this.errorMessage = 'ไม่พบข้อมูลผู้ใช้ กรุณาเข้าสู่ระบบใหม่';
            return;
        }

        this.isLoading = true;
        this.errorMessage = '';
        this.successMessage = '';

        this.http.post(`${this.API_URL}/users/${currentUser.id}/force-change-password`, {
            oldPassword: currentPassword,
            newPassword: newPassword
        }).subscribe({
            next: () => {
                this.isLoading = false;
                this.successMessage = 'เปลี่ยนรหัสผ่านสำเร็จ! กำลังนำท่านกลับไปหน้าเดิม...';
                this.cdr.detectChanges();

                setTimeout(() => {
                    // Go back to previous page or dashboard
                    window.history.back();
                }, 2000);
            },
            error: (error) => {
                this.isLoading = false;
                this.handleError(error);
                this.cdr.detectChanges();
            }
        });
    }

    private handleError(error: any): void {
        if (error.status === 401) {
            this.errorMessage = 'รหัสผ่านปัจจุบันไม่ถูกต้อง กรุณาตรวจสอบและลองใหม่อีกครั้ง';
        } else if (error.status === 400) {
            this.errorMessage = error.error?.message || 'ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบและลองใหม่อีกครั้ง';
        } else if (error.status === 0) {
            this.errorMessage = 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต';
        } else {
            this.errorMessage = 'เกิดข้อผิดพลาดที่ไม่คาดคิด กรุณาลองใหม่อีกครั้ง';
        }
    }

    private markFormGroupTouched(): void {
        Object.keys(this.changePasswordForm.controls).forEach(key => {
            const control = this.changePasswordForm.get(key);
            control?.markAsTouched();
        });
    }

    // Getters
    get currentPassword() { return this.changePasswordForm.get('currentPassword'); }
    get newPassword() { return this.changePasswordForm.get('newPassword'); }
    get confirmPassword() { return this.changePasswordForm.get('confirmPassword'); }

    // Password requirement helpers (regex literals can't be used in Angular templates)
    get newPasswordValue(): string { return this.newPassword?.value || ''; }
    get hasMinLength(): boolean { return this.newPasswordValue.length >= 8; }
    get hasLowercase(): boolean { return /[a-z]/.test(this.newPasswordValue); }
    get hasUppercase(): boolean { return /[A-Z]/.test(this.newPasswordValue); }
    get hasNumber(): boolean { return /[0-9]/.test(this.newPasswordValue); }

    goBack(): void {
        window.history.back();
    }
}
